import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Package } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import JumlatyLogo from "../components/JumlatyLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MujahezSignup = () => {
  const navigate = useNavigate();
  const { mujahezSignup } = useAuth();

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError("حجم الصورة يجب أن يكون أقل من 5 ميغابايت");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!fullName.trim()) { setError("الاسم مطلوب"); return; }
    if (password.length < 6) { setError("كلمة السر يجب أن تكون 6 أحرف على الأقل"); return; }
    if (password !== confirmPassword) { setError("كلمة السر غير متطابقة"); return; }

    setLoading(true);
    try {
      let photoPath = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const upRes = await axios.post(`${API}/auth/upload-driver-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoPath = upRes.data.path;
      }
      const userObj = await mujahezSignup({
        full_name: fullName,
        phone,
        password,
        photo_path: photoPath,
      });
      toast.success(`أهلاً ${userObj.full_name}! تم إنشاء حساب المجهّز`);
      navigate("/mujahez-jumlaty");
    } catch (err) {
      const msg = err.response?.data?.detail || "حدث خطأ، حاول مرة أخرى";
      setError(typeof msg === "string" ? msg : "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <JumlatyLogo size="xl" showTagline />
          </Link>
          <p className="text-[#1E3A5F] font-medium mt-3 flex items-center justify-center gap-2">
            <Package className="w-4 h-4" />
            تسجيل مجهّز جديد
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-white/85 backdrop-blur-md border border-[#BAE6FD] rounded-3xl p-6 sm:p-8 shadow-xl">
          <div className="flex justify-center">
            <label htmlFor="mujahez-photo" className="cursor-pointer flex flex-col items-center gap-2">
              {photoPreview ? (
                <img src={photoPreview} alt="صورة" className="w-24 h-24 rounded-full object-cover border-4 border-[#7ED321]" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#F0FDF4] border-4 border-dashed border-[#7ED321] flex items-center justify-center">
                  <Camera className="w-8 h-8 text-[#7ED321]" />
                </div>
              )}
              <span className="text-xs text-[#5C6E64]">صورة المجهّز</span>
              <input id="mujahez-photo" type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" data-testid="mujahez-photo-input" />
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">اسم المجهّز *</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="mujahez-signup-name"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7ED321] bg-white"
              placeholder="الاسم الثلاثي" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">رقم الموبايل *</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))} data-testid="mujahez-signup-phone"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7ED321] bg-white"
              placeholder="07XXXXXXXXX" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">كلمة السر *</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="mujahez-signup-password"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7ED321] bg-white"
              placeholder="6 أحرف على الأقل" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111814] mb-2">تأكيد كلمة السر *</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="mujahez-signup-confirm-password"
              className="w-full px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#7ED321] bg-white"
              placeholder="أعد كتابة كلمة السر" required />
          </div>

          {error && <p data-testid="mujahez-signup-error" className="text-[#E85D45] text-sm">{error}</p>}

          <motion.button type="submit" disabled={loading} whileTap={{ scale: 0.98 }} data-testid="mujahez-signup-submit"
            className="w-full bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-3 rounded-full transition-colors disabled:opacity-50">
            {loading ? "جاري إنشاء الحساب..." : "إنشاء حساب مجهّز"}
          </motion.button>

          <p className="text-center text-sm text-[#5C6E64]">
            لديك حساب بالفعل؟{" "}
            <Link to="/login" className="text-[#2D4A3E] font-medium hover:underline">تسجيل الدخول</Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default MujahezSignup;
